import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import Navbar from "../components/Layout/Navbar";
import Footerpage from "../components/Layout/Footer";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: "mdi:home-outline" },
  { id: "1", label: "1. Information We Collect", icon: "mdi:file-document-outline" },
  { id: "2", label: "2. Account Information", icon: "mdi:account-outline" },
  { id: "3", label: "3. Payment Information", icon: "mdi:credit-card-outline" },
  { id: "4", label: "4. Internship Information", icon: "mdi:briefcase-outline" },
  { id: "5", label: "5. Communications", icon: "mdi:email-outline" },
  { id: "6", label: "6. Automatically Collected Info", icon: "mdi:chart-box-outline" },
  { id: "7", label: "7. Cookies and Similar Tech", icon: "mdi:cookie-outline" },
  { id: "8", label: "8. How We Use Your Information", icon: "mdi:cog-outline" },
  { id: "9", label: "9. Legal and Compliance", icon: "mdi:gavel" },
  { id: "10", label: "10. How We Share Information", icon: "mdi:share-variant-outline" },
  { id: "11", label: "11. Data Processors", icon: "mdi:server-outline" },
  { id: "12", label: "12. Data Security", icon: "mdi:shield-lock-outline" },
  { id: "13", label: "13. Data Retention", icon: "mdi:database-outline" },
  { id: "14", label: "14. Your Privacy Rights", icon: "mdi:account-check-outline" },
  { id: "15", label: "15. Withdrawal of Consent", icon: "mdi:cancel" },
  { id: "16", label: "16. Marketing Communications", icon: "mdi:bullhorn-outline" },
  { id: "17", label: "17. Children's Privacy", icon: "mdi:account-child-outline" },
  { id: "18", label: "18. Educational & Career Info", icon: "mdi:school-outline" },
  { id: "19", label: "19. Social Media", icon: "mdi:share-outline" },
  { id: "20", label: "20. Testimonials", icon: "mdi:comment-quote-outline" },
  { id: "21", label: "21. International Data", icon: "mdi:earth" },
  { id: "22", label: "22. Data Breaches", icon: "mdi:alert-outline" },
  { id: "23", label: "23. Third-Party Websites", icon: "mdi:link-variant" },
  { id: "24", label: "24. Changes to This Policy", icon: "mdi:update" },
  { id: "25", label: "25. Grievance / Contact", icon: "mdi:phone-outline" },
  { id: "26", label: "26. Consent", icon: "mdi:check-circle-outline" },
];

const PrivacyPolicy = () => {
  const [active, setActive] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 140;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.offsetTop <= scrollY) {
          setActive(SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 110, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Navbar />

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4ff] via-[#f5f8ff] to-white pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="pointer-events-none absolute right-0 top-20 h-[420px] w-[420px] rounded-full bg-blue-200/30 blur-[100px]" />
        <div className="pointer-events-none absolute left-10 top-40 h-[280px] w-[280px] rounded-full bg-indigo-100/40 blur-[80px]" />

        <div className="relative mx-auto flex max-w-6xl items-center justify-between gap-12 px-5 lg:px-8">
          <div className="max-w-xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-200/60 bg-white/70 px-3.5 py-1.5 text-[12px] font-medium text-slate-600 backdrop-blur-sm">
              <Icon icon="mdi:shield-check" className="h-3.5 w-3.5 text-blue-600" />
              Trust · Transparency · Your Data
            </div>

            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
              How we collect, use, share and protect your personal information.
            </p>

            <p className="mt-5 text-[13px] text-slate-500">
              Last Updated: 24 September 2026 &nbsp;|&nbsp; Effective Date: 24 September 2026
            </p>
          </div>

          {/* Right illustration */}
          <div className="relative hidden lg:block">
            <div className="relative h-56 w-64">
              <div className="absolute right-0 top-0 h-48 w-40 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-50 shadow-lg" />
              <div className="absolute bottom-4 left-0 flex h-36 w-36 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-xl shadow-blue-500/30">
                <Icon icon="mdi:shield-lock" className="h-16 w-16 text-white" />
              </div>
              <div className="absolute -right-4 top-8 rounded-xl border border-white/60 bg-white/90 px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-md backdrop-blur">
                <div className="flex items-center gap-1.5">
                  <Icon icon="mdi:shield-check" className="h-3.5 w-3.5 text-blue-600" />
                  Your Data
                </div>
                <div className="mt-0.5 text-[10px] font-normal text-slate-500">Our Priority</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Main ─── */}
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <div className="flex gap-10">
          {/* Sidebar */}
          <aside className="sticky top-28 hidden h-fit w-60 shrink-0 lg:block">
            <nav className="max-h-[calc(100vh-140px)] space-y-0.5 overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[12.5px] font-medium transition-all ${
                    active === s.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon
                    icon={s.icon}
                    className={`h-4 w-4 shrink-0 ${active === s.id ? "text-blue-600" : "text-slate-400"}`}
                  />
                  <span className="truncate">{s.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1 space-y-7">
            {/* Overview */}
            <div id="overview" className="flex items-start gap-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                <Icon icon="mdi:shield-check" className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">Your Privacy Matters</h3>
                <p className="mt-1 text-[13.5px] leading-relaxed text-slate-600">
                  WeIntern Pvt Ltd (“WeIntern”, “we”, “us”, or “our”) respects your privacy and is committed to
                  protecting the personal information you provide while using our website, platform, courses,
                  internship programs and related services. This Privacy Policy applies to we-intern.in and related
                  WeIntern services.
                </p>
              </div>
            </div>

            {/* 1 */}
            <section id="1">
              <h2 className="mb-1 text-xl font-bold text-slate-900">1. Information We Collect</h2>
              <p className="mb-5 text-[14px] text-slate-500">
                Depending on how you interact with WeIntern, we may collect different categories of information.
              </p>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <Icon icon="mdi:account" className="h-4.5 w-4.5 text-blue-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">1.1 Personal Information</h3>
                </div>
                <p className="mb-4 text-[13.5px] text-slate-500">This may include:</p>
                <div className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2">
                  {[
                    "Full name", "Email address", "Mobile number", "Date of birth or age where necessary",
                    "Gender where voluntarily provided", "City and state", "Address where necessary",
                    "Profile photograph", "Educational institution", "Course/degree", "Academic year",
                    "Skills", "Resume/CV", "Work experience", "Career interests", "Internship preferences",
                    "Portfolio links", "LinkedIn profile", "GitHub profile", "Other professional information",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-[13.5px] text-slate-700">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 2 */}
            <section id="2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50">
                    <Icon icon="mdi:account-circle" className="h-4.5 w-4.5 text-emerald-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">2. Account Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  When you create or use a WeIntern account, we may collect username, login credentials, account
                  information, profile information, enrollment information, program participation information, course
                  progress and assignment information.
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                  We use this information to provide and administer your account and services.
                </p>
              </div>
            </section>

            {/* 3 */}
            <section id="3">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-50">
                    <Icon icon="mdi:credit-card" className="h-4.5 w-4.5 text-violet-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">3. Payment Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  When you make a payment, we may collect information necessary to identify and reconcile the
                  transaction, such as transaction ID, payment status, amount, date and time, payment method and
                  order/enrollment information.
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                  Payment card, banking or other sensitive payment credentials may be processed directly by third-party
                  payment providers. WeIntern generally does not need to store your complete card or banking
                  credentials on our own systems.
                </p>
              </div>
            </section>

            {/* 4 */}
            <section id="4">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50">
                    <Icon icon="mdi:briefcase" className="h-4.5 w-4.5 text-rose-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">4. Internship Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  If you apply for or participate in an internship, we may collect resume, educational details,
                  technical skills, interview information, application information, internship role,
                  attendance/participation information, assignment submissions, project information,
                  performance-related information, mentor feedback, certificate information and placement-related
                  information.
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                  This information may be required to administer the internship and related career services.
                </p>
              </div>
            </section>

            {/* 5 */}
            <section id="5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-50">
                    <Icon icon="mdi:email" className="h-4.5 w-4.5 text-cyan-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">5. Communications</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  When you communicate with us, we may collect information contained in emails, support requests,
                  contact forms, WhatsApp communications, calls, feedback forms, surveys, chat messages and other
                  communications. We use this information to respond to you and improve our services.
                </p>
              </div>
            </section>

            {/* 6 */}
            <section id="6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50">
                    <Icon icon="mdi:chart-box" className="h-4.5 w-4.5 text-amber-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">6. Automatically Collected Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  When you visit our website, certain technical information may be collected automatically, including
                  IP address, browser type, device type, operating system, pages visited, approximate location derived
                  from technical information, referral source, date and time of access, website interactions and session
                  information. This information helps us understand website usage, maintain security and improve
                  performance.
                </p>
              </div>
            </section>

            {/* 7 */}
            <section id="7">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-50">
                    <Icon icon="mdi:cookie" className="h-4.5 w-4.5 text-orange-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">7. Cookies and Similar Technologies</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  WeIntern may use cookies, pixels, local storage and similar technologies for website functionality,
                  authentication, security, analytics, performance measurement, personalization, understanding user
                  behavior and marketing where permitted. You may be able to control cookies through your browser
                  settings. Disabling certain cookies may affect website functionality.
                </p>
              </div>
            </section>

            {/* 8 */}
            <section id="8">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50">
                    <Icon icon="mdi:cog" className="h-4.5 w-4.5 text-indigo-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">8. How We Use Your Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We may process your information for purposes including providing services, creating accounts,
                  processing enrollments, providing training, administering internships, providing mentorship, managing
                  projects, issuing certificates, providing placement assistance, processing payments and providing
                  customer support.
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                  We may also use information to understand how users interact with our platform, identify technical
                  problems, improve courses, improve user experience, develop new programs, measure program
                  effectiveness and improve our website.
                </p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
                  We may contact you regarding enrollment, account activity, classes, internship updates, assignments,
                  projects, payments, certificates, support requests and important service changes. Where permitted and
                  where appropriate consent has been provided, we may also send promotional offers, program
                  announcements, webinars, events, new course information and career opportunities.
                </p>
              </div>
            </section>

            {/* 9 */}
            <section id="9">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100">
                    <Icon icon="mdi:gavel" className="h-4.5 w-4.5 text-slate-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">9. Legal and Compliance Purposes</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We may process information where necessary to comply with applicable law, respond to lawful requests,
                  prevent fraud, investigate misuse, enforce our Terms, protect our rights, protect users and maintain
                  platform security.
                </p>
              </div>
            </section>

            {/* 10 */}
            <section id="10">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-50">
                    <Icon icon="mdi:share-variant" className="h-4.5 w-4.5 text-teal-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">10. How We Share Information</h3>
                </div>
                <p className="mb-4 text-[13.5px] text-slate-600">
                  We do not sell your personal information as a commercial product. We may share information where
                  reasonably necessary with:
                </p>
                <div className="space-y-3">
                  {[
                    { title: "Service Providers", text: "Hosting providers, payment processors, email providers, analytics providers, CRM systems, cloud services, communication providers, video-conferencing providers and customer-support providers." },
                    { title: "Trainers and Mentors", text: "Relevant information may be shared with trainers, mentors or project managers where necessary to provide your program." },
                    { title: "Employers and Recruitment Partners", text: "Where you participate in placement services or expressly apply for an opportunity, relevant professional information may be shared with potential employers or recruitment partners as appropriate." },
                    { title: "Legal and Regulatory Authorities", text: "We may disclose information when required by applicable law, legal process or lawful government request." },
                    { title: "Business Transactions", text: "Information may be transferred as part of a merger, acquisition, restructuring, financing or sale of business assets, subject to applicable law." },
                  ].map((item) => (
                    <div key={item.title}>
                      <p className="text-[13.5px] font-semibold text-slate-800">{item.title}</p>
                      <p className="text-[13.5px] leading-relaxed text-slate-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 11 */}
            <section id="11">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <Icon icon="mdi:server" className="h-4.5 w-4.5 text-blue-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">11. Data Processors and Third Parties</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Third-party providers may process personal information on our behalf. Examples may include payment
                  providers, analytics platforms, hosting providers, communication platforms and recruitment systems.
                  These providers may have their own privacy policies and security practices. We take reasonable steps
                  to work with service providers that are appropriate for the services they provide and to require
                  appropriate handling of personal information where applicable.
                </p>
              </div>
            </section>

            {/* 12 */}
            <section id="12">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <Icon icon="mdi:shield-lock" className="h-4.5 w-4.5 text-blue-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">12. Data Security</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We take reasonable technical and organizational measures to protect personal information against
                  unauthorized access, accidental loss, misuse, unauthorized disclosure, alteration and destruction.
                  However, no internet-based system can be guaranteed to be completely secure. You are also responsible
                  for maintaining the confidentiality of your account credentials.
                </p>
              </div>
            </section>

            {/* 13 */}
            <section id="13">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100">
                    <Icon icon="mdi:database" className="h-4.5 w-4.5 text-slate-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">13. Data Retention</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We retain personal information only for as long as reasonably necessary for the purposes for which it
                  was collected, including providing services, maintaining records, resolving disputes, enforcing
                  agreements, complying with legal obligations, preventing fraud and maintaining business records.
                  Retention periods may vary depending on the type of information and applicable legal requirements.
                  When information is no longer required, we may delete, anonymize or securely dispose of it, subject to
                  applicable law.
                </p>
              </div>
            </section>

            {/* 14 */}
            <section id="14">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50">
                    <Icon icon="mdi:account-check" className="h-4.5 w-4.5 text-emerald-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">14. Your Privacy Rights</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Subject to applicable law, you may have rights regarding your personal information, including the
                  ability to request access to information we hold about you, request correction of inaccurate
                  information, request deletion where legally applicable, withdraw consent where processing is based on
                  consent, request information about processing and raise a grievance regarding our handling of personal
                  information. Certain rights may be subject to legal limitations. To exercise a privacy-related right,
                  contact us using the details provided below.
                </p>
              </div>
            </section>

            {/* 15 */}
            <section id="15">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50">
                    <Icon icon="mdi:cancel" className="h-4.5 w-4.5 text-rose-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">15. Withdrawal of Consent</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Where we process your personal information based on consent, you may withdraw that consent, subject to
                  applicable law. Withdrawal of consent will not affect processing that was already carried out lawfully
                  before withdrawal. You understand that withdrawing consent may affect our ability to provide certain
                  services where the information is necessary for those services.
                </p>
              </div>
            </section>

            {/* 16 */}
            <section id="16">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50">
                    <Icon icon="mdi:bullhorn" className="h-4.5 w-4.5 text-amber-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">16. Marketing Communications</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We may communicate with you about our programs, services, events and offers where permitted by law.
                  You may unsubscribe from promotional email communications using the unsubscribe mechanism provided in
                  the communication. For SMS, WhatsApp or other communication channels, you may contact us to request
                  that promotional communications be stopped, subject to applicable requirements and legitimate
                  operational communications.
                </p>
              </div>
            </section>

            {/* 17 */}
            <section id="17">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-pink-50">
                    <Icon icon="mdi:account-child" className="h-4.5 w-4.5 text-pink-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">17. Children's Privacy</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  WeIntern primarily provides services to students and learners. We do not knowingly seek unnecessary
                  personal information from children. Where applicable law requires parental or guardian consent for
                  processing a child's personal data, WeIntern will seek to comply with those requirements. If you
                  believe that a child has provided personal information to us without the required authorization,
                  please contact us.
                </p>
              </div>
            </section>

            {/* 18 */}
            <section id="18">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50">
                    <Icon icon="mdi:school" className="h-4.5 w-4.5 text-indigo-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">18. Educational and Career Information</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Because WeIntern provides education and career-development services, we may process information
                  relating to education, skills, projects, career interests, resumes, internship participation,
                  assessments and placement activities. Such information is used to provide relevant education and
                  career services. We do not guarantee that providing this information will result in employment or
                  placement.
                </p>
              </div>
            </section>

            {/* 19 */}
            <section id="19">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-50">
                    <Icon icon="mdi:share" className="h-4.5 w-4.5 text-cyan-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">19. Social Media</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  WeIntern may maintain profiles or pages on platforms such as LinkedIn, Instagram, YouTube, Facebook
                  and WhatsApp. Interactions with these platforms may be governed by the privacy policies of the
                  respective platforms. Information voluntarily posted publicly by you may become publicly accessible.
                  Please avoid posting sensitive personal information publicly.
                </p>
              </div>
            </section>

            {/* 20 */}
            <section id="20">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-50">
                    <Icon icon="mdi:comment-quote" className="h-4.5 w-4.5 text-violet-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">20. Testimonials and Promotional Content</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  If you voluntarily provide a testimonial, review, photograph, video or other promotional content to
                  WeIntern, we may use it for marketing or promotional purposes where you have provided appropriate
                  permission or where otherwise legally permitted. You should not submit content that you do not have
                  the right to provide.
                </p>
              </div>
            </section>

            {/* 21 */}
            <section id="21">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-50">
                    <Icon icon="mdi:earth" className="h-4.5 w-4.5 text-teal-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">21. International Data Processing</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Some of our service providers may process information outside India. Where personal information is
                  transferred or processed outside India, WeIntern will take steps required by applicable law.
                </p>
              </div>
            </section>

            {/* 22 */}
            <section id="22">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50">
                    <Icon icon="mdi:alert" className="h-4.5 w-4.5 text-rose-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">22. Data Breaches</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  If WeIntern becomes aware of a personal-data breach requiring notification under applicable law, we
                  will take appropriate steps to investigate, contain and address the incident and provide notifications
                  where legally required.
                </p>
              </div>
            </section>

            {/* 23 */}
            <section id="23">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100">
                    <Icon icon="mdi:link-variant" className="h-4.5 w-4.5 text-slate-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">23. Third-Party Websites</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  Our website may contain links to third-party websites. WeIntern is not responsible for the privacy
                  practices of those websites. You should review the privacy policy of any third-party website before
                  providing personal information.
                </p>
              </div>
            </section>

            {/* 24 */}
            <section id="24">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-50">
                    <Icon icon="mdi:update" className="h-4.5 w-4.5 text-amber-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">24. Changes to This Privacy Policy</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  We may update this Privacy Policy periodically. The updated version will be published on this page
                  with a revised “Last Updated” date. If material changes are made, we may provide additional notice
                  where required by applicable law.
                </p>
              </div>
            </section>

            {/* 25 */}
            <section id="25">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <Icon icon="mdi:phone" className="h-4.5 w-4.5 text-blue-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">25. Grievance / Privacy Contact</h3>
                </div>
                <p className="mb-4 text-[13.5px] font-medium text-slate-800">WeIntern Pvt Ltd</p>
                <div className="space-y-2 text-[13.5px] text-slate-600">
                  <div className="flex gap-3"><span className="w-32 shrink-0 font-medium text-slate-800">Email</span> privacy@we-intern.in</div>
                  <div className="flex gap-3"><span className="w-32 shrink-0 font-medium text-slate-800">Support</span> support@we-intern.in</div>
                  <div className="flex gap-3"><span className="w-32 shrink-0 font-medium text-slate-800">Website</span> https://we-intern.in</div>
                  <div className="flex gap-3"><span className="w-32 shrink-0 font-medium text-slate-800">Phone</span> +91 7414974582</div>
                  <div className="flex gap-3"><span className="w-32 shrink-0 font-medium text-slate-800">Subject line</span> “Privacy Request – WeIntern”</div>
                </div>
                <p className="mt-4 text-[13.5px] leading-relaxed text-slate-600">
                  We will review and respond to requests in accordance with applicable law.
                </p>
              </div>
            </section>

            {/* 26 */}
            <section id="26">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50">
                    <Icon icon="mdi:check-circle" className="h-4.5 w-4.5 text-emerald-600" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-slate-900">26. Consent</h3>
                </div>
                <p className="text-[13.5px] leading-relaxed text-slate-600">
                  By using the WeIntern website or services, you acknowledge that you have read this Privacy Policy.
                  Where applicable law requires consent for a particular processing activity, WeIntern will seek consent
                  through an appropriate mechanism.
                </p>
              </div>
            </section>

            {/* Bottom CTA */}
            <div className="flex flex-col items-start justify-between gap-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-6 sm:flex-row sm:items-center">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
                  <Icon icon="mdi:shield-check" className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-slate-900">
                    We are committed to keeping your information safe and secure.
                  </h3>
                  <p className="mt-1 text-[13px] text-slate-600">
                    If you have any questions about this Privacy Policy, feel free to contact us.
                  </p>
                </div>
              </div>
              <Link
                to="/#contact"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-blue-600 bg-white px-5 py-2.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-600 hover:text-white"
              >
                Contact Us
                <Icon icon="mdi:arrow-right" className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footerpage />
    </div>
  );
};

export default PrivacyPolicy;